import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Star, Home, Loader2, CheckCircle, AlertCircle, Calendar, MapPin } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Star Rating Input Component
const StarRatingInput = ({ value, onChange }) => {
  const [hover, setHover] = useState(0);
  const labels = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];
  
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(0)}
            className="p-1 transition-transform hover:scale-110 focus:outline-none"
            data-testid={`star-${star}`}
          >
            <Star
              className={`w-10 h-10 transition-colors ${
                star <= (hover || value) 
                  ? 'text-yellow-400 fill-yellow-400' 
                  : 'text-gray-300'
              }`}
            />
          </button>
        ))}
      </div>
      {(hover || value) > 0 && (
        <p className="text-center text-lg font-medium text-gray-600">
          {labels[hover || value]}
        </p>
      )}
    </div>
  );
};

export default function ReviewSubmitPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submissionInfo, setSubmissionInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  
  const [formData, setFormData] = useState({
    rating: 0,
    title: '',
    content: '',
    guest_name: '',
    guest_email: ''
  });

  // Load review submission info
  useEffect(() => {
    const loadInfo = async () => {
      try {
        const resp = await fetch(`${API_URL}/api/reviews/submit/${token}`);
        let data;
        
        // Safely parse JSON response
        try {
          const text = await resp.text();
          data = JSON.parse(text);
        } catch (parseErr) {
          // Fall back to a friendly error message
          setError('Invalid or expired review link');
          return;
        }
        
        if (!resp.ok) {
          throw new Error(data.detail || 'Invalid or expired review link');
        }
        setSubmissionInfo(data);
        // Pre-fill guest name if available
        if (data.guest_name) {
          setFormData(f => ({ ...f, guest_name: data.guest_name }));
        }
      } catch (err) {
        // Clean up error messages for display
        const message = err.message || 'An error occurred';
        if (message.includes('body stream') || message.includes('Response body') || message.includes('parse')) {
          setError('Invalid or expired review link');
        } else {
          setError(message);
        }
      } finally {
        setLoading(false);
      }
    };
    
    loadInfo();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.rating === 0) {
      setError('Please select a star rating');
      return;
    }
    if (!formData.content.trim()) {
      setError('Please write a review');
      return;
    }
    if (!formData.guest_name.trim()) {
      setError('Please enter your name');
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      const resp = await fetch(`${API_URL}/api/reviews/submit/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.detail || 'Failed to submit review');
      }
      
      const result = await resp.json();
      setSubmitResult(result);
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-[#FF385C] mx-auto mb-4" />
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Error state (invalid/expired link)
  if (error && !submissionInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Unable to Load Review</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button 
            onClick={() => navigate('/marketplace')}
            className="bg-[#FF385C] hover:bg-[#E31C5F] text-white rounded-xl"
          >
            Go to Marketplace
          </Button>
        </div>
      </div>
    );
  }

  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h1>
          <p className="text-gray-600 mb-2">
            Your review has been submitted successfully.
          </p>
          {submitResult?.status === 'pending_approval' && (
            <p className="text-sm text-gray-500 mb-6">
              Your review will be published after the host reviews it.
            </p>
          )}
          {submitResult?.status === 'published' && (
            <p className="text-sm text-emerald-600 mb-6">
              Your review is now live!
            </p>
          )}
          <div className="flex gap-3 justify-center">
            <Button 
              onClick={() => navigate('/marketplace')}
              variant="outline"
              className="rounded-xl"
            >
              Browse Properties
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Review form
  return (
    <div className="min-h-screen bg-gray-50" data-testid="review-submit-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <div className="w-8 h-8 bg-gradient-to-br from-[#FF385C] to-[#E31C5F] rounded-lg flex items-center justify-center">
            <Home className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-gray-900">iHostPMS</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Property Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8">
          <div className="flex gap-4">
            {submissionInfo.property_photo ? (
              <img 
                src={submissionInfo.property_photo} 
                alt={submissionInfo.property_name}
                className="w-24 h-24 rounded-xl object-cover"
              />
            ) : (
              <div className="w-24 h-24 rounded-xl bg-gray-100 flex items-center justify-center">
                <Home className="w-8 h-8 text-gray-400" />
              </div>
            )}
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900 mb-1">
                {submissionInfo.property_name}
              </h2>
              {submissionInfo.property_city && (
                <p className="text-gray-500 flex items-center gap-1 text-sm mb-2">
                  <MapPin className="w-4 h-4" />
                  {submissionInfo.property_city}
                </p>
              )}
              {(submissionInfo.check_in || submissionInfo.check_out) && (
                <p className="text-gray-500 flex items-center gap-1 text-sm">
                  <Calendar className="w-4 h-4" />
                  {submissionInfo.check_in?.slice(0, 10)} - {submissionInfo.check_out?.slice(0, 10)}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Review Form */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2 text-center">
            How was your stay?
          </h1>
          <p className="text-gray-500 text-center mb-8">
            Share your experience to help future guests
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Star Rating */}
            <div className="flex justify-center">
              <StarRatingInput 
                value={formData.rating} 
                onChange={(v) => setFormData(f => ({ ...f, rating: v }))} 
              />
            </div>

            {/* Review Title (optional) */}
            <div>
              <Label className="text-sm font-medium text-gray-700">
                Review Title (optional)
              </Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(f => ({ ...f, title: e.target.value }))}
                placeholder="Summarize your experience"
                className="mt-1"
                data-testid="review-title"
              />
            </div>

            {/* Review Content */}
            <div>
              <Label className="text-sm font-medium text-gray-700">
                Your Review *
              </Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData(f => ({ ...f, content: e.target.value }))}
                placeholder="Tell us about your stay. What did you love? Any tips for future guests?"
                rows={5}
                className="mt-1"
                data-testid="review-content"
              />
            </div>

            {/* Guest Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-gray-700">
                  Your Name *
                </Label>
                <Input
                  value={formData.guest_name}
                  onChange={(e) => setFormData(f => ({ ...f, guest_name: e.target.value }))}
                  placeholder="John Smith"
                  className="mt-1"
                  data-testid="guest-name"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700">
                  Email (optional)
                </Label>
                <Input
                  type="email"
                  value={formData.guest_email}
                  onChange={(e) => setFormData(f => ({ ...f, guest_email: e.target.value }))}
                  placeholder="john@example.com"
                  className="mt-1"
                  data-testid="guest-email"
                />
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Submit */}
            <Button
              type="submit"
              disabled={submitting || formData.rating === 0}
              className="w-full bg-gradient-to-r from-[#FF385C] to-[#E31C5F] hover:from-[#E31C5F] hover:to-[#C91848] text-white rounded-xl h-12 text-base font-semibold"
              data-testid="submit-review-btn"
            >
              {submitting ? (
                <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Submitting...</>
              ) : (
                'Submit Review'
              )}
            </Button>

            <p className="text-xs text-gray-500 text-center">
              By submitting, you agree that your review may be published publicly.
            </p>
          </form>
        </div>
      </main>
    </div>
  );
}
